import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formControlClass, formLabelClass } from './fieldStyles';

const MENU_MAX_HEIGHT = 240;
const MENU_GAP = 4;

function normalizeLabel(value) {
  return String(value || '').trim();
}

/** Case-insensitive A→Z sort by option label. */
export function sortOptionsByLabel(options = []) {
  return [...options].sort((a, b) =>
    normalizeLabel(a.label).localeCompare(normalizeLabel(b.label), undefined, {
      sensitivity: 'base',
      numeric: true,
    })
  );
}

/**
 * Searchable dropdown: options are A–Z, typing filters by prefix
 * ("A" → names starting with A, "Ab" → starting with Ab).
 *
 * options: [{ value, label, disabled? }]
 */
export function SearchableSelect({
  label,
  error,
  hint,
  options = [],
  value = '',
  onChange,
  placeholder = 'Select…',
  emptyMessage = 'No matches',
  className = '',
  id,
  disabled = false,
  required = false,
  /** When false, hide the clear (×) control (selection can still be changed by picking another option). */
  clearable = true,
}) {
  const uid = useId().replace(/:/g, '');
  const inputId = id || (label ? `search-sel-${uid}` : undefined);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuRect, setMenuRect] = useState(null);

  const sorted = useMemo(() => sortOptionsByLabel(options), [options]);

  const selected = useMemo(
    () => sorted.find((o) => String(o.value) === String(value)) || null,
    [sorted, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((o) => normalizeLabel(o.label).toLowerCase().startsWith(q));
  }, [sorted, query]);

  const measure = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - MENU_GAP;
    const above = r.top - MENU_GAP;
    const dropUp = below < Math.min(MENU_MAX_HEIGHT, 160) && above > below;
    setMenuRect({
      left: r.left,
      width: r.width,
      top: dropUp ? undefined : r.bottom + MENU_GAP,
      bottom: dropUp ? window.innerHeight - r.top + MENU_GAP : undefined,
      maxHeight: Math.max(120, Math.min(MENU_MAX_HEIGHT, dropUp ? above : below)),
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuRect(null);
      return undefined;
    }
    measure();
    const onViewportChange = () => measure();
    window.addEventListener('scroll', onViewportChange, true);
    window.addEventListener('resize', onViewportChange);
    return () => {
      window.removeEventListener('scroll', onViewportChange, true);
      window.removeEventListener('resize', onViewportChange);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(0);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open, query]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-opt-index="${activeIndex}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, open]);

  const commit = (opt) => {
    if (!opt || opt.disabled) return;
    onChange?.(String(opt.value));
    setOpen(false);
    setQuery('');
  };

  const clear = () => {
    onChange?.('');
    setQuery('');
    setOpen(false);
  };

  const openPicker = () => {
    if (disabled) return;
    setQuery('');
    setOpen(true);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openPicker();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(filtered[activeIndex]);
    } else if (e.key === 'Escape') {
      // Keep Escape from also closing a surrounding modal.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      setQuery('');
    }
  };

  const displayValue = open ? query : (selected?.label || '');

  return (
    <div className="w-full min-w-0" ref={rootRef}>
      {label ? (
        <label htmlFor={inputId} className={formLabelClass}>
          {label}
          {required ? <span className="text-rose-600"> *</span> : null}
        </label>
      ) : null}
      <div className="relative">
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${inputId}-list`}
          aria-autocomplete="list"
          aria-activedescendant={
            open && filtered[activeIndex] ? `${inputId}-opt-${activeIndex}` : undefined
          }
          className={formControlClass({
            error: Boolean(error),
            className: `pr-16 ${className}`.trim(),
          })}
          placeholder={placeholder}
          value={displayValue}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={openPicker}
          onClick={openPicker}
          onKeyDown={onKeyDown}
        />
        <div className="absolute inset-y-0 right-1.5 flex items-center gap-0.5">
          {clearable && value ? (
            <button
              type="button"
              tabIndex={-1}
              disabled={disabled}
              className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                clear();
              }}
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ) : null}
          <span className="pointer-events-none p-1.5 text-slate-400" aria-hidden>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </div>

        {open && menuRect ? createPortal(
          <ul
            id={`${inputId}-list`}
            ref={listRef}
            role="listbox"
            style={{
              position: 'fixed',
              left: menuRect.left,
              width: menuRect.width,
              top: menuRect.top,
              bottom: menuRect.bottom,
              maxHeight: menuRect.maxHeight,
            }}
            className="z-[1000] overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-xl ring-1 ring-slate-900/5"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2.5 text-sm text-slate-500">{emptyMessage}</li>
            ) : (
              filtered.map((opt, i) => {
                const isActive = i === activeIndex;
                const isSelected = String(opt.value) === String(value);
                return (
                  <li
                    key={String(opt.value)}
                    id={`${inputId}-opt-${i}`}
                    data-opt-index={i}
                    role="option"
                    aria-selected={isSelected}
                    className={[
                      'cursor-pointer px-3 py-2 text-sm',
                      isActive ? 'bg-primary-50 text-primary-900' : 'text-slate-800',
                      isSelected ? 'font-semibold' : 'font-normal',
                      opt.disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-primary-50/80',
                    ].join(' ')}
                    onMouseEnter={() => setActiveIndex(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      commit(opt);
                    }}
                  >
                    {opt.label}
                  </li>
                );
              })
            )}
          </ul>,
          document.body
        ) : null}
      </div>
      {error ? (
        <p className="mt-1.5 text-sm text-rose-600" role="alert">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

export default SearchableSelect;
